import { useEffect, useRef, useState } from 'react';

import { cn } from 'lib/helpers/utility';

import useLivePreviewToggle from 'components/practice/Settings/components/HealthcareProgram/lib/useLivePreviewToggle';

import LivePreview from './LivePreview/LivePreview';
import Settings from './PaymentSettings/PaymentSettings';


const PaymentProgramBody = ({ draft, dispatch, activePhaseField, setActivePhaseField }) => {
  const isLivePreviewVisible = useLivePreviewToggle();
  const containerRef = useRef(null);
  const [settingsWidth, setSettingsWidth] = useState(50);
  const dragCleanupRef = useRef(null);

  useEffect(() => () => dragCleanupRef.current?.(), []);

  const clampWidth = (pct) => Math.min(80, Math.max(20, pct));

  const handleResize = (e) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const onMove = (ev) => {
      const rect = container.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setSettingsWidth(clampWidth(pct));
    };
    const cleanup = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      dragCleanupRef.current = null;
    };
    const onUp = cleanup;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    dragCleanupRef.current = cleanup;
  };

  const handleResizeKeyDown = (e) => {
    const STEP = 2;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setSettingsWidth((prev) => clampWidth(prev - STEP));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      setSettingsWidth((prev) => clampWidth(prev + STEP));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setSettingsWidth(20);
    } else if (e.key === 'End') {
      e.preventDefault();
      setSettingsWidth(80);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 gap-2 overflow-hidden px-4 pb-5 pt-4">
      <div
        ref={containerRef}
        className={cn(
          'flex min-h-0 flex-1 flex-row justify-center overflow-hidden rounded-lg',
          isLivePreviewVisible && 'border border-solid border-neutral-200'
        )}>
        <div
          className={cn('flex min-h-0 min-w-0', !isLivePreviewVisible && 'max-w-[856px]')}
          style={{ width: isLivePreviewVisible ? `${settingsWidth}%` : '100%' }}>
          <Settings
            draft={draft}
            dispatch={dispatch}
            activePhaseField={activePhaseField}
            setActivePhaseField={setActivePhaseField}
            className="w-full"
          />
        </div>
        {isLivePreviewVisible && (
          <div
            onMouseDown={handleResize}
            onKeyDown={handleResizeKeyDown}
            tabIndex={0}
            className="group relative w-1 shrink-0 cursor-col-resize bg-neutral-200 hover:bg-primary-400 focus:bg-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize settings panel"
            aria-valuemin={20}
            aria-valuemax={80}
            aria-valuenow={Math.round(settingsWidth)}>
            <div className="absolute inset-y-0 -left-1 -right-1" />
          </div>
        )}
        <div className="flex min-h-0 min-w-0 flex-1">
          <LivePreview draft={draft} activePhaseField={activePhaseField} />
        </div>
      </div>
    </div>
  );
};

export default PaymentProgramBody;
